import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import logo from "../../../assets/images/Support_logo.png";
import phoneIcon from "../../../assets/images/Support_call.png";
import SupportCallBg from "../../../assets/images/Support_call_bg.png";
import HamburgerMenuHeader from "../../Header/DeliveryMenuHeader";
import { useTranslation } from "react-i18next";

const DeliverySupport = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  /* -------------------- STATE -------------------- */
  const [formData, setFormData] = useState({
    about: "",
    subject: "",
    customMessage: "",
  });

  const [selectedIssue, setSelectedIssue] = useState("");
  const [result, setResult] = useState("");
  const [errors, setErrors] = useState({});

  /* -------------------- TRANSLATION HELPERS -------------------- */

  // Always return array safely
  const getSafeArray = (key) => {
    const value = t(key, { returnObjects: true });
    return Array.isArray(value) ? value : [];
  };

  const WebAppQuestions = t("deliveryPartnerFaqs.webApp", {
    returnObjects: true,
  });
  const LockerQuestions = t("deliveryPartnerFaqs.lockerScreen", {
    returnObjects: true,
  });
  const otherQuestions = t("deliveryPartnerFaqs.otherIssues", {
    returnObjects: true,
  });

  const extractQuestions = (section) => {
    if (!section || typeof section !== "object") return [];
    return Object.keys(section)
      .filter((key) => key.startsWith("q"))
      .map((key) => section[key]);
  };

  const getIssueData = () => {
    if (formData.about === "webApp") {
      return {
        title: WebAppQuestions.title || "Web App",
        questions: extractQuestions(WebAppQuestions),
      };
    }
    if (formData.about === "lockerScreen") {
      return {
        title: LockerQuestions.title || "Locker Screen",
        questions: extractQuestions(LockerQuestions),
      };
    }
    if (formData.about === "otherIssues") {
      return {
        title: otherQuestions.title || "Other Issues",
        questions: extractQuestions(otherQuestions),
      };
    }
    return { title: "", questions: [] };
  };

  const { title: issueTitle, questions: issueQuestions } = getIssueData();

  /* -------------------- HANDLERS -------------------- */

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));

    // Clear selected quick issue when changing category
    if (name === "about") {
      setSelectedIssue("");
    }
  };

  const validateForm = () => {
    const newErrors = {};

    const hasCategory = !!formData.about;
    const hasQuickIssue = !!selectedIssue;
    const hasMessage = !!formData.customMessage.trim();
    const hasSubject = !!formData.subject.trim();

    // 1️⃣ Message validation
    if (!hasQuickIssue && !hasMessage) {
      newErrors.message = "Please select a quick issue or type your message.";
    }

    // 2️⃣ Subject validation (STRICTLY per your table)
    if (
      (!hasCategory && !hasQuickIssue && !hasMessage && !hasSubject) || // nothing selected
      (hasMessage && !hasSubject) // message typed only
    ) {
      newErrors.subject = "Subject is required.";
    }

    // 3️⃣ Category validation (only when nothing selected)
    if (!hasCategory && !hasQuickIssue && !hasMessage) {
      newErrors.about = "Please select where the issue occurred.";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setResult("Sending...");
    let finalMessage = selectedIssue
      ? `Selected Issue: ${selectedIssue}\n\n`
      : "";
    finalMessage +=
      formData.customMessage.trim() || "(No additional details provided)";

    const form = new FormData();
    form.append("access_key", "9c78354d-81f1-46e9-a853-291123304c44");
    form.append("subject", formData.subject.trim() || "Support Request");
    form.append("about", formData.about);
    form.append("message", finalMessage);
    form.append("category", formData.about);

    try {
      const response = await fetch("https://api.web3forms.com/submit", {
        method: "POST",
        body: form,
      });

      const data = await response.json();

      if (data.success) {
        setResult(
          "Thank you! Your support request has been sent successfully.",
        );
        // Reset everything
        setFormData({ about: "", subject: "", customMessage: "" });
        setSelectedIssue("");
        setErrors({});
      } else {
        setResult(data.message || "Failed to send. Please try again.");
      }
    } catch (err) {
      setResult("Network error. Please check your internet.");
    }
  };

  /* -------------------- UI -------------------- */

  return (
    <div className="w-full flex flex-col items-center bg-[#FDFEFF] min-h-screen">
      <HamburgerMenuHeader title={t("support")} />

      {/* Support Call Banner */}
      <div
        className="mt-12 w-full max-w-[409px] h-[89.37px] flex justify-between items-center px-4 bg-cover bg-center rounded-full"
        style={{ backgroundImage: `url(${SupportCallBg})` }}
      >
        <img src={logo} alt="Logo" className="w-[60px] h-[60px]" />
        <div className="flex flex-col text-white">
          <span className="text ">+91 9892233193</span>
          <span className="text-sm text-center">{t("customerSupport")}</span>
        </div>
        <a href="tel:+919892233193">
          <img src={phoneIcon} alt="Call" className="w-[91px] h-[54px]" />
        </a>
      </div>

      <form
        onSubmit={handleSubmit}
        className="mt-10 w-full max-w-[620px] p-4 flex flex-col gap-6"
      >
        <label className="text-center text-[30px] font-dongle">
          {t("haveQuestions")}
        </label>

        {/* Category Selection - ALWAYS VISIBLE */}
        <div className="border rounded p-4">
          <label className="font-semibold block mb-3">{t("whereIssue")}</label>

          {/* UPDATED RADIO BUTTON SECTION */}
          <div className="flex flex-wrap gap-4">
            {[
              { key: "webApp", value: "webApp" },
              { key: "lockerScreen", value: "lockerScreen" },
              { key: "otherIssues", value: "otherIssues" },
            ].map((option) => (
              <label
                key={option.key}
                className="flex items-center gap-2 cursor-pointer"
              >
                <input
                  type="radio"
                  name="about"
                  value={option.value}
                  checked={formData.about === option.value}
                  onChange={handleChange}
                  className="w-5 h-5"
                />
                <span>
                  {option.key === "webApp" &&
                    t("deliveryPartnerFaqs.webApp.title")}
                  {option.key === "lockerScreen" &&
                    t("deliveryPartnerFaqs.lockerScreen.title")}
                  {option.key === "otherIssues" &&
                    t("deliveryPartnerFaqs.otherIssues.title")}
                </span>
              </label>
            ))}
          </div>

          {errors.about && (
            <p className="text-red-500 text-sm mt-2">{errors.about}</p>
          )}

          {/* Show quick issues only if a category is selected */}
          {formData.about && issueQuestions.length > 0 && (
            <div className="mt-6">
              {/* <p className="font-medium mb-3">{issueTitle}</p> */}

              <div className="flex flex-col gap-3">
                {issueQuestions.map((q, index) => (
                  <label
                    key={index}
                    className="flex items-center gap-3 border border-gray-200 rounded-lg px-4 py-3 hover:bg-gray-50 cursor-pointer transition-colors"
                  >
                    <input
                      type="radio"
                      name="quickIssue"
                      value={q}
                      checked={selectedIssue === q}
                      onChange={(e) => setSelectedIssue(e.target.value)}
                      className="w-5 h-5"
                    />
                    <span className="text-[16px]">{q}</span>
                  </label>
                ))}
              </div>
            </div>
          )}
        </div>

        <input
          type="text"
          name="subject"
          placeholder={t("subject")}
          value={formData.subject}
          onChange={handleChange}
          className="border p-3 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
        {errors.subject && (
          <p className="text-red-500 text-sm -mt-2">{errors.subject}</p>
        )}

        <textarea
          name="customMessage"
          placeholder={t("messagePlaceholder")}
          value={formData.customMessage}
          onChange={handleChange}
          className="border p-3 rounded-lg h-[140px] resize-none focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
        {errors.message && (
          <p className="text-red-500 text-sm -mt-2">{errors.message}</p>
        )}

        <button
          type="submit"
          className="bg-[#2699FF] text-white py-3 rounded-full font-medium hover:bg-blue-600 transition-colors"
        >
          {t("submit")}
        </button>

        {result && (
          <p className="text-center font-semibold text-lg mt-2">{result}</p>
        )}
      </form>
    </div>
  );
};

export default DeliverySupport;
